import { useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import MobileStepper from '@mui/material/MobileStepper';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepIcon from '@mui/material/StepIcon';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { FieldPath, useFormContext } from 'react-hook-form';

import { PublicPackage } from '@drivingdoc/api-client/generated/types';

import SelectSectionFieldArray from './components/SelectSectionFieldArray';
import { AgreementStep } from './steps/AgreementStep';
import { ParentInformationStep } from './steps/ParentInformationStep';
import { PasswordStep } from './steps/PasswordStep';
import { StudentInformationStep } from './steps/StudentInformationStep';
import { RegistrationFormOutputValues } from './utils/validationSchema';

const FormStepIcon = styled(StepIcon)(({ theme }) => ({
  '&.MuiStepIcon-root.Mui-completed': {
    color: theme.palette.success.main,
  },
}));

interface RegistrationFormStepperProps {
  package: PublicPackage;
  isSubmitting: boolean;
}

type StepData = {
  label: string;
  fields: FieldPath<RegistrationFormOutputValues>[];
  optional?: boolean;
};

const steps: StepData[] = [
  {
    label: 'Select Course Sections',
    fields: ['courses'],
  },
  {
    label: 'Student Information',
    fields: [
      'first_name',
      'middle_name',
      'last_name',
      'nickname',
      'email',
      'sex',
      'date_of_birth',
      'high_school',
      'user_phones',
      'user_addresses',
    ],
  },
  {
    label: 'Parent Information',
    fields: ['user_parents'],
  },
  {
    label: 'Login Information',
    fields: ['password'],
  },
  {
    label: 'Terms of Service',
    fields: ['package_agreement'],
  },
];

function RegistrationFormStepper(props: RegistrationFormStepperProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const {
    formState: { errors },
    trigger,
  } = useFormContext();

  // Determine if a step has errors
  const stepError = (stepIndex: number) => {
    const fields = steps[stepIndex].fields;
    return fields.some((field) => !!errors[field]);
  };

  const handleNext = async () => {
    const fields = steps[activeStep].fields;
    if (fields.length > 0) {
      // Trigger validation for the current step's fields
      const isValid = await trigger(fields, { shouldFocus: true });
      if (!isValid) {
        return; // Prevent moving to the next step if validation fails
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  // effect to scroll to top when the route changes
  useEffect(() => {
    // otherwise scroll to the top
    window.scrollTo(0, 0);
  }, [activeStep]);

  useEffect(() => {
    console.log(errors);
  }, [errors]);

  return (
    <Stack p={4} gap={2}>
      {/* Registration form title */}
      <Typography variant={'h4'} sx={{ fontWeight: 700 }} gutterBottom align={'center'}>
        Register For {props.package?.title}
      </Typography>

      {/* Desktop Stepper */}
      {!isMobile && (
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step, index) => (
            <Step key={step.label}>
              <StepLabel slots={{ stepIcon: FormStepIcon }} error={stepError(index)}>
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      )}

      <Divider />

      {/* <Typography>{steps[activeStep].label}</Typography> */}

      {/* Display content for the active step */}
      <Box py={2}>
        {activeStep === 0 && <SelectSectionFieldArray name="courses" courses={props?.package?.courses} />}
        {activeStep === 1 && <StudentInformationStep />}
        {activeStep === 2 && <ParentInformationStep />}
        {activeStep === 3 && <PasswordStep />}
        {activeStep === 4 && <AgreementStep package={props.package} />}
      </Box>

      {/* Mobile Stepper */}
      {isMobile && (
        <MobileStepper
          variant="text"
          steps={steps.length}
          position="static"
          activeStep={activeStep}
          backButton={
            <Button size="small" onClick={handleBack} disabled={activeStep === 0}>
              Back
            </Button>
          }
          nextButton={
            activeStep === steps.length - 1 ? (
              <Button size="small" type="submit" loading={props.isSubmitting}>
                Register
              </Button>
            ) : (
              <Button size="small" onClick={handleNext}>
                Next
              </Button>
            )
          }
        />
      )}

      {/* Container for desktop stepper controls */}
      {!isMobile && (
        <Box display="flex" flexDirection="row" justifyContent="space-between" pt={2}>
          {/* Back button (disabled on first step) */}
          <Button variant="contained" color="inherit" disabled={activeStep === 0} onClick={handleBack}>
            Back
          </Button>

          {/* Next button (hidden on last step) */}
          {activeStep < steps.length - 1 && (
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          )}

          {/* Submit button (only visible on last step) */}
          {activeStep === steps.length - 1 && (
            <Button variant="contained" type="submit" loading={props.isSubmitting}>
              Register
            </Button>
          )}
        </Box>
      )}
    </Stack>
  );
}

export default RegistrationFormStepper;
